import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, CalendarDays, Ban, RotateCcw, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import BookingOverrideDialog from "@/components/admin/BookingOverrideDialog";
import ScheduleSettings from "@/components/admin/ScheduleSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function detailMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(", ") || fallback;
  if (d && typeof d === "object") return d.msg || fallback;
  return fallback;
}

function eachDate(start, end) {
  const out = [];
  const d = new Date(start + "T00:00:00");
  const last = new Date(end + "T00:00:00");
  while (d <= last) { out.push(new Date(d)); d.setDate(d.getDate() + 1); }
  return out;
}

export default function AvailabilityManager({ token, onAuthError }) {
  const [subTab, setSubTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [vid, setVid] = useState("");
  const [ranges, setRanges] = useState([]);
  const [released, setReleased] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const [overrideBooking, setOverrideBooking] = useState(null);
  const [prep, setPrep] = useState({ lead_enabled: true, lead_hours: 4 });
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/booking-config`).then((r) => setPrep(r.data)).catch(() => {});
    axios.get(`${API}/vehicles?all=true`, auth).then((r) => {
      setVehicles(r.data);
      if (r.data[0]) setVid(r.data[0].id);
    }).catch((e) => { if (e.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const savePrep = async (patch) => {
    setPrep((p) => ({ ...p, ...patch }));
    try { await axios.patch(`${API}/booking-config`, patch, auth); toast.success("Prep window saved."); }
    catch { toast.error("Could not save."); }
  };

  const vehicle = vehicles.find((v) => v.id === vid);

  const loadRanges = () => {
    if (!vehicle) return;
    axios.get(`${API}/availability/manage?vehicle_id=${vid}`, auth).then((r) => {
      setRanges(r.data.ranges || []);
      setReleased(r.data.released || []);
    }).catch((e) => { if (e.response?.status === 401) onAuthError?.(); });
    axios.get(`${API}/blocks?vehicle_id=${vid}`, auth).then((r) => setBlocks(r.data));
  };
  useEffect(() => { loadRanges(); /* eslint-disable-next-line */ }, [vid, vehicles.length]);

  const { booked, blocked } = useMemo(() => {
    const b = [], bl = [];
    ranges.forEach((rg) => { (rg.type === "blocked" ? bl : b).push(...eachDate(rg.start, rg.end)); });
    return { booked: b, blocked: bl };
  }, [ranges]);

  const addBlock = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Pick a start and end date."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/blocks`, { vehicle_id: vid, ...form }, auth);
      toast.success("Dates blocked.");
      setForm({ start_date: "", end_date: "", reason: "" });
      loadRanges();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(detailMsg(err, "Could not block dates."));
    } finally { setSaving(false); }
  };

  const removeBlock = async (id) => {
    try { await axios.delete(`${API}/blocks/${id}`, auth); toast.success("Block removed."); loadRanges(); }
    catch { toast.error("Could not remove block."); }
  };

  const restoreDates = async (bid, force = false) => {
    try {
      await axios.post(`${API}/bookings/${bid}/restore-dates`, { force }, auth);
      toast.success("Dates restored to the calendar.");
      loadRanges();
    } catch (err) {
      if (err.response?.status === 409 && !force) {
        if (window.confirm(detailMsg(err, "These dates now overlap another reservation. Restore anyway?"))) return restoreDates(bid, true);
      } else toast.error(detailMsg(err, "Could not restore dates."));
    }
  };

  const bookingForDate = (ds) => {
    const rg = ranges.find((r) => r.type === "booked" && r.booking_id && r.start <= ds && ds <= r.end);
    return rg ? { ...rg, vehicle: vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : rg.vehicle } : null;
  };

  const fmtDate = (d) => {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), da = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
  };

  const toggleDate = async (date, override = false) => {
    if (!date || !vid) return;
    const ds = fmtDate(date);
    if (!override && booked.some((b) => fmtDate(b) === ds)) {
      if (!window.confirm(`${ds} has a customer booking. Block/unblock it anyway?`)) return;
      return toggleDate(date, true);
    }
    try {
      const { data } = await axios.post(`${API}/blocks/toggle-date`, { vehicle_id: vid, date: ds, override }, auth);
      toast.success(data.action === "blocked" ? `Blocked ${ds}.` : `Unblocked ${ds}.`);
      loadRanges();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else if (err.response?.status === 409 && !override) {
        if (window.confirm(detailMsg(err, "This date has a booking."))) return toggleDate(date, true);
      } else toast.error(detailMsg(err, "Could not update that date."));
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  const cls = "bg-transparent border-b border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm rounded-none [color-scheme:dark]";

  return (
    <div data-testid="admin-availability">
      <div className="flex gap-2 mb-6">
        <button data-testid="availability-subtab-vehicles" onClick={() => setSubTab("vehicles")}
          className={`text-sm font-semibold px-4 py-2 rounded-md border ${subTab === "vehicles" ? "bg-[#F2B705] text-black border-[#F2B705]" : "bg-[#0C0C0C] text-white/70 border-white/10"}`}>
          Vehicle Calendar
        </button>
        <button data-testid="availability-subtab-schedule" onClick={() => setSubTab("schedule")}
          className={`text-sm font-semibold px-4 py-2 rounded-md border ${subTab === "schedule" ? "bg-[#F2B705] text-black border-[#F2B705]" : "bg-[#0C0C0C] text-white/70 border-white/10"}`}>
          Pickup/Return Hours
        </button>
      </div>

      {subTab === "schedule" ? (
        <ScheduleSettings token={token} onAuthError={onAuthError} />
      ) : (
      <>
      {/* Booking Prep Window */}
      <div data-testid="prep-window-card" className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#F2B705]/15 text-[#F2B705]"><Clock size={18} /></div>
            <div>
              <div className="text-white font-semibold">Booking Prep Window</div>
              <div className="text-white/50 text-sm">Block same-day pickups sooner than this many hours out — gives you time to prep the car. Central Time.</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-white/60 text-sm">Require</label>
            <input type="number" step="1" min="0" max="72" data-testid="prep-hours-input"
              className="w-20 px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]"
              value={prep.lead_hours}
              onChange={(e) => setPrep((p) => ({ ...p, lead_hours: e.target.value }))}
              onBlur={(e) => savePrep({ lead_hours: Number(e.target.value) || 0 })} disabled={!prep.lead_enabled} />
            <span className="text-white/60 text-sm">hours</span>
            <button data-testid="prep-toggle-btn" onClick={() => savePrep({ lead_enabled: !prep.lead_enabled })}
              className={`text-xs px-3 py-2 rounded-md font-semibold border ${prep.lead_enabled ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-white/50 border-white/10"}`}>
              {prep.lead_enabled ? "ON" : "OFF (instant)"}
            </button>
          </div>
        </div>
      </div>

      <p className="text-white/50 text-sm mb-1">Booked dates (gold) are auto-blocked from new bookings. Add your own blocks for maintenance or personal use (red).</p>
      <p className="text-[#F2B705] text-sm mb-4 font-semibold">Tap any date on the calendar to block or unblock it instantly.</p>
      <select data-testid="availability-vehicle-select" value={vid} onChange={(e) => setVid(e.target.value)}
        className="bg-[#0C0C0C] border border-white/10 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#F2B705] mb-5">
        {vehicles.map((v) => <option key={v.id} value={v.id} className="bg-[#0A0A0A]">{v.year} {v.make} {v.model}</option>)}
      </select>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4 w-fit">
          <Calendar
            numberOfMonths={1}
            onDayClick={(day) => {
              const ds = fmtDate(day);
              const bk = bookingForDate(ds);
              if (bk) { setOverrideBooking(bk); return; }
              toggleDate(day);
            }}
            modifiers={{ booked, blocked }}
            modifiersClassNames={{
              booked: "bg-[#F2B705] text-black rounded-md font-bold",
              blocked: "bg-red-500 text-white rounded-md font-bold",
            }}
            className="text-white cursor-pointer"
          />
          <div className="flex gap-4 mt-2 text-xs text-white/50 px-3">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#F2B705]" /> Booked</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500" /> Blocked</span>
          </div>
          <p className="text-white/40 text-xs mt-2 px-3">Tap a <span className="text-[#F2B705]">booked</span> date for owner override (release / edit / transfer / cancel). Tap an open date to block it.</p>
        </div>

        <div>
          <h4 className="flex items-center gap-2 font-display font-bold mb-3"><Ban size={16} /> Block Dates Manually</h4>
          <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4 space-y-3" data-testid="availability-block-form">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[11px] text-white/45 block">From</label><input type="date" data-testid="block-start" className={cls} value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} /></div>
              <div><label className="text-[11px] text-white/45 block">To</label><input type="date" data-testid="block-end" className={cls} value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} /></div>
            </div>
            <div><label className="text-[11px] text-white/45 block">Reason</label><input data-testid="block-reason" className={cls + " w-full"} placeholder="e.g. Oil change, personal use" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <button data-testid="block-add" onClick={addBlock} disabled={saving} className="bg-[#F2B705] text-black text-sm font-bold px-4 py-2 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <CalendarDays size={14} />} Block These Dates
            </button>
          </div>

          <h4 className="font-display font-bold mt-6 mb-2 text-sm flex items-center gap-2"><Ban size={14} className="text-red-400" /> Blocked Dates</h4>
          <div className="space-y-1.5" data-testid="blocked-dates-list">
            {blocks.length === 0 && <p className="text-white/35 text-sm">No manual blocks. Block dates above for maintenance or personal use.</p>}
            {blocks.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-2 bg-[#0A0A0A] border border-red-500/20 rounded px-3 py-2 text-sm">
                <span><span className="text-red-400">{b.start_date} → {b.end_date}</span>{b.reason ? <span className="text-white/50"> · {b.reason}</span> : ""}</span>
                <button data-testid={`unblock-${b.id}`} onClick={() => removeBlock(b.id)}
                  className="shrink-0 text-xs flex items-center gap-1.5 border border-red-400/40 text-red-400 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors">
                  <Trash2 size={13} /> Unblock
                </button>
              </div>
            ))}
          </div>

          <h4 className="font-display font-bold mt-6 mb-2 text-sm flex items-center gap-2"><RotateCcw size={14} className="text-emerald-400" /> Released Bookings <span className="text-white/40 font-normal">(dates freed — restore if needed)</span></h4>
          <div className="space-y-1.5" data-testid="released-bookings-list">
            {released.length === 0 && <p className="text-white/35 text-sm">No released bookings for this vehicle.</p>}
            {released.map((r) => (
              <div key={r.booking_id} className="flex items-center justify-between gap-2 bg-[#0A0A0A] border border-emerald-500/20 rounded px-3 py-2 text-sm">
                <span><span className="text-emerald-400">{r.pickup_date} → {r.return_date}</span> · {r.name}{r.released_by ? <span className="text-white/40"> · by {r.released_by}</span> : ""}</span>
                <button data-testid={`restore-${r.booking_id}`} onClick={() => restoreDates(r.booking_id)}
                  className="shrink-0 text-xs flex items-center gap-1.5 border border-emerald-400/40 text-emerald-400 px-3 py-1.5 rounded hover:bg-emerald-500 hover:text-white transition-colors">
                  <RotateCcw size={13} /> Restore
                </button>
              </div>
            ))}
          </div>

          <h4 className="font-display font-bold mt-6 mb-2 text-sm">Upcoming for this vehicle</h4>
          <div className="space-y-1.5">
            {ranges.length === 0 && <p className="text-white/35 text-sm">No bookings or blocks.</p>}
            {ranges.map((rg, i) => {
              const blk = rg.type === "blocked" ? blocks.find((b) => b.start_date === rg.start) : null;
              const isBooking = rg.type === "booked" && rg.booking_id;
              return (
                <div key={i} className={`flex items-center justify-between gap-2 bg-[#0A0A0A] border border-white/10 rounded px-3 py-2 text-sm ${isBooking ? "cursor-pointer hover:border-[#F2B705]/40" : ""}`}
                  onClick={isBooking ? () => setOverrideBooking({ ...rg, vehicle: vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : rg.vehicle }) : undefined}
                  data-testid={isBooking ? `booking-row-${rg.booking_id}` : undefined}>
                  <span><span className={rg.type === "blocked" ? "text-red-400" : "text-[#F2B705]"}>{rg.start} → {rg.end}</span> · {rg.label}{isBooking ? <span className="text-white/30 text-xs ml-1">(tap to manage)</span> : ""}</span>
                  {blk && <button data-testid={`block-delete-${blk.id}`} onClick={(e) => { e.stopPropagation(); removeBlock(blk.id); }}
                    className="shrink-0 text-xs flex items-center gap-1.5 border border-red-400/40 text-red-400 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors">
                    <Trash2 size={13} /> Unblock
                  </button>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {overrideBooking && (
        <BookingOverrideDialog token={token} booking={overrideBooking} vehicles={vehicles}
          onClose={() => setOverrideBooking(null)}
          onDone={() => { setOverrideBooking(null); loadRanges(); }} />
      )}
      </>
      )}
    </div>
  );
}
